import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';

// Helper function (outside the component)
function getYouTubeId(url) {
  if (!url) return null;
  const regExp = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i;
  const match = url.match(regExp);
  return match ? match[1] : null;
}

const PublicFilms = () => {
  const [films, setFilms] = useState([]);
  const [filteredFilms, setFilteredFilms] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showUnreviewed, setShowUnreviewed] = useState(false); // State for filtering unreviewed films
  const [hideZeroTokens, setHideZeroTokens] = useState(true); // State for hiding films with 0 tokens
  const [showTopReviewers, setShowTopReviewers] = useState(false); // State for filtering films by top reviewers
  const [topReviewers, setTopReviewers] = useState([]); // State for storing the list of top reviewers
  const [error, setError] = useState('');
  const [page, setPage] = useState(1); // Current page
  const [pageSize] = useState(10); // Items per page
  const [totalPages, setTotalPages] = useState(1); // Total number of pages
  const navigate = useNavigate();

  const feedbackCategories = [
    'Writing',
    'Cinematography',
    'Editing',
    'Art-Design',
    'Costuming',
    'Sound-Design',
    'Directing',
  ]; // List of feedback categories

  const handleLeaveReview = (filmId) => {
    navigate(`/films/${filmId}/review`);
  };

  useEffect(() => {
    // Scroll to the top of the page when the component loads
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchPublicFilms = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL || 'https://api.eatourshorts.com'}/films/public?page=${page}&pageSize=${pageSize}`);
        const allFilms = response.data.films; // Fetch all films from the backend

        // Apply the initial filtering logic for hideZeroTokens
        const filtered = allFilms.filter((film) => {
          const matchesTokens = !hideZeroTokens || Number(film.uploader_tokens) > 0; // Filter films with 0 tokens if hideZeroTokens is true
          return matchesTokens;
        });


        setFilms(response.data.films); // Assuming the backend returns { films, totalPages }
        setFilteredFilms(filtered); // Initialize filtered films
        setTotalPages(response.data.totalPages); // Set total pages from backend
      } catch (err) {
        console.error('Error fetching public films:', err);
        setError('Failed to fetch public films. Please try again later.');
      }
    };

    const fetchTopReviewers = async () => {
  try {
    const response = await axios.get(`${process.env.REACT_APP_API_URL || 'https://api.eatourshorts.com'}/reviewers/top`); // Backend route for top reviewers
    setTopReviewers(response.data.topReviewers); // Store the list of top reviewers in the correct state
  } catch (err) {
    console.error('Error fetching top reviewers:', err);
    setError('Failed to fetch top reviewers. Please try again later.');
  }
};

    fetchPublicFilms();
    fetchTopReviewers();

  }, [page, pageSize]); // Refetch films when page or pageSize changes

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    const filtered = films.filter((film) => {
      const matchesTitle = film.title.toLowerCase().includes(query);
      const matchesGenre = film.genre.toLowerCase().includes(query);
      const matchesUploader = film.uploader_username.toLowerCase().includes(query);
      const matchesCategory =
        !selectedCategory || film.feedback_categories.includes(selectedCategory);
      const matchesUnreviewed = !showUnreviewed || Number(film.review_count) === 0; // Filter unreviewed films if checkbox is selected
      const matchesTokens = !hideZeroTokens || Number(film.uploader_tokens) > 0; // Filter films with 0 tokens if checkbox is selected
      const matchesTopReviewers = !showTopReviewers || showTopReviewers.includes(film.uploader_username); // Filter films by top reviewers if checkbox is selected
      return (matchesTitle || matchesGenre || matchesUploader) && matchesCategory && matchesUnreviewed && matchesTokens && matchesTopReviewers;
    });

    setFilteredFilms(filtered);
  };

  const handleCategoryChange = (e) => {
    const category = e.target.value;
    setSelectedCategory(category);

    const filtered = films.filter((film) => {
      const matchesTitle = film.title.toLowerCase().includes(searchQuery);
      const matchesGenre = film.genre.toLowerCase().includes(searchQuery);
      const matchesUploader = film.uploader_username.toLowerCase().includes(searchQuery);
      const matchesCategory =
        !category || film.feedback_categories.includes(category);
       const matchesUnreviewed = !showUnreviewed || Number(film.review_count) === 0; // Filter unreviewed films if checkbox is selected
       const matchesTokens = !hideZeroTokens || Number(film.uploader_tokens) > 0; // Apply token filter only if toggled on
       const matchesTopReviewers = !showTopReviewers || topReviewers.includes(film.uploader_username); // Apply top reviewers filter only if toggled on
      return (matchesTitle || matchesGenre || matchesUploader) && matchesCategory && matchesUnreviewed && matchesTokens && matchesTopReviewers;
    });

    setFilteredFilms(filtered);
  };

const handleUnreviewedToggle = () => {
  const newShowUnreviewed = !showUnreviewed; // Toggle the state
  setShowUnreviewed(newShowUnreviewed);

  const filtered = films.filter((film) => {
    const matchesTitle = film.title.toLowerCase().includes(searchQuery);
    const matchesGenre = film.genre.toLowerCase().includes(searchQuery);
    const matchesUploader = film.uploader_username.toLowerCase().includes(searchQuery);
    const matchesCategory =
      !selectedCategory || film.feedback_categories.includes(selectedCategory);
    const matchesUnreviewed = !newShowUnreviewed || Number(film.review_count) <= 2; // Apply unreviewed filter only if toggled on
    const matchesTokens = !hideZeroTokens || Number(film.uploader_tokens) > 0; // Apply token filter only if toggled on
    const matchesTopReviewers = !showTopReviewers || topReviewers.includes(film.uploader_username); // Apply top reviewers filter only if toggled on
    return (matchesTitle || matchesGenre || matchesUploader) && matchesCategory && matchesUnreviewed && matchesTokens && matchesTopReviewers;
  });

  setFilteredFilms(filtered);
};

  const handleHideZeroTokensToggle = () => {
    const newHideZeroTokens = !hideZeroTokens; // Toggle the state
    setHideZeroTokens(newHideZeroTokens);

    const filtered = films.filter((film) => {
      const matchesTitle = film.title.toLowerCase().includes(searchQuery);
      const matchesGenre = film.genre.toLowerCase().includes(searchQuery);
      const matchesUploader = film.uploader_username.toLowerCase().includes(searchQuery);
      const matchesCategory =
        !selectedCategory || film.feedback_categories.includes(selectedCategory);
      const matchesUnreviewed = !showUnreviewed || Number(film.review_count) <= 2; // Apply unreviewed filter only if toggled on
      const matchesTokens = !newHideZeroTokens || Number(film.uploader_tokens) > 0; // Apply token filter only if toggled on
      const matchesTopReviewers = !showTopReviewers || topReviewers.includes(film.uploader_username); // Apply top reviewers filter only if toggled on
      return (matchesTitle || matchesGenre || matchesUploader) && matchesCategory && matchesUnreviewed && matchesTokens;
    });

    setFilteredFilms(filtered);
  };


  const handleTopReviewersToggle = () => {
    const newTopReviewers = !showTopReviewers // Toggle the state
    setShowTopReviewers(newTopReviewers);

    const filtered = films.filter((film) => {
      const matchesTitle = film.title.toLowerCase().includes(searchQuery);
      const matchesGenre = film.genre.toLowerCase().includes(searchQuery);
      const matchesUploader = film.uploader_username.toLowerCase().includes(searchQuery);
      const matchesCategory =
        !selectedCategory || film.feedback_categories.includes(selectedCategory);
      const matchesUnreviewed = !showUnreviewed || Number(film.review_count) <= 2; // Apply unreviewed filter only if toggled on
      const matchesTokens = !hideZeroTokens || Number(film.uploader_tokens) > 0; // Apply token filter only if toggled on
      const matchesTopReviewers = !newTopReviewers || topReviewers.includes(film.uploader_username); // Apply top reviewers filter only if toggled on
      return (matchesTitle || matchesGenre || matchesUploader) && matchesCategory && matchesUnreviewed && matchesTokens && matchesTopReviewers;
    });

    setFilteredFilms(filtered);
  };


  return (
    <div
      style={{
        padding: '20px',
        fontFamily: 'Arial, sans-serif',
        color: '#333',
        background: 'linear-gradient(to bottom, #f0f8ff, #d6eaff)', // Subtle gradient background
        minHeight: '100vh', // Ensures the background covers the full viewport height
      }}
    >
      {/*
      <h1 style={{ textAlign: 'center', color: '#0056b3', marginBottom: '30px', fontSize: '2.5rem' }}>
        Public Films
      </h1>
        */}


      {error && <p style={{ color: 'red', textAlign: 'center', fontWeight: 'bold' }}>{error}</p>}
  
      {/* Filters and Search */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: '15px',
          marginBottom: '30px',
          padding: '10px',
          backgroundColor: '#a5d6a7',
          borderRadius: '10px',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        }}
      >
        <input
          type="text"
          placeholder="Search by title, genre, or uploader..."
          value={searchQuery}
          onChange={handleSearch}
          style={{
            padding: '10px',
            width: '400px',
            fontSize: '1.2rem',
            border: '1px solid #ccc',
            borderRadius: '5px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          }}
        />
  
        <select
          value={selectedCategory}
          onChange={handleCategoryChange}
          style={{
            padding: '10px',
            border: '1px solid #ccc',
            borderRadius: '5px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          }}
        >
          <option value="">All Categories</option>
          {feedbackCategories.map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </select>
          
        <div style={{ flexBasis: '100%', height: 0 }} />

        <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <input
            type="checkbox"
            checked={showUnreviewed}
            onChange={handleUnreviewedToggle}
            style={{ transform: 'scale(1.2)' }}
          />
          <span>Only show films with very little feedback</span>
        </label>
  
        <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <input
            type="checkbox"
            checked={hideZeroTokens}
            onChange={handleHideZeroTokensToggle}
            style={{ transform: 'scale(1.2)' }}
          />
          <span> Only show films from uploaders with tokens</span>
        </label>
  
        <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <input
            type="checkbox"
            checked={showTopReviewers}
            onChange={handleTopReviewersToggle}
            style={{ transform: 'scale(1.2)' }}
          />
          <span>Only show films from top 5 reviewers</span>
        </label>
      </div>
  
      {/* Film List */}
      {filteredFilms.length > 0 ? (
        <ul
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
            gap: '20px',
            padding: '0',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          {filteredFilms.map((film, index) => (
            <li
              key={film.id}
              style={{
                listStyle: 'none',
                border: '1px solid #ccc',
                borderRadius: '10px',
                padding: '15px',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9f9f9',
                transition: 'transform 0.2s, box-shadow 0.2s',
                maxWidth: '100%',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.02)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              <div
                style={{
                  backgroundColor: '#e3f2fd', // Light blue background
                  border: '2px solid #1976d2', // Blue border
                  borderRadius: '7px',
                  padding: '12px 15px',
                  marginBottom: '14px',
                  fontWeight: 'bold',
                  fontSize: '1.35rem',
                  color: '#0d47a1',
                  boxShadow: '0 2px 6px rgba(25, 118, 210, 0.07)',
                  textAlign: 'center',
                  letterSpacing: '0.5px',
                }}
              >
                {film.title}
              </div>
         <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center', // Center horizontally
              gap: '40px',              // Increase space between items
              fontSize: '0.95rem',
              marginBottom: '12px',
              flexWrap: 'wrap',
            }}
          >
            <span>
              <strong>Genre:</strong> {film.genre}
            </span>
            <span>
              <strong>Length:</strong> {film.length} minutes
            </span>
          </div>
           
             {film.youtube_link && getYouTubeId(film.youtube_link) ? (
                <div style={{ margin: '10px 0' }}>
                  <iframe
                    width="100%"
                    height="280"
                    src={`https://www.youtube.com/embed/${getYouTubeId(film.youtube_link)}`}
                    title="YouTube video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{ borderRadius: '10px', maxWidth: '100%' }}
                  ></iframe>
                </div>
              ) : (
                <p>
                  <strong>YouTube Link:</strong>{' '}
                  {film.youtube_link ? (
                    <a
                      href={film.youtube_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#0056b3', textDecoration: 'none' }}
                    >
                      Watch on YouTube
                    </a>
                  ) : (
                    'No link available'
                  )}
                </p>
              )}

              <div
                style={{
                  backgroundColor: '#e3f2fd', // Light blue background
                  border: '2px solid #1976d2', // Blue border
                  borderRadius: '7px',
                  padding: '10px 15px',
                  margin: '12px 0',
                  fontWeight: 'bold',
                  fontSize: '1.01rem',
                  color: '#0d47a1',
                  boxShadow: '0 2px 6px rgba(25, 118, 210, 0.07)',
                  textAlign: 'center',
                }}
              >
                <span style={{ color: '#1976d2', fontWeight: 'bold', fontSize: '1.1em' }}>
                  Feedback Categories:
                </span>{' '}
                {film.feedback_categories && film.feedback_categories.length > 0
                  ? film.feedback_categories.join(', ')
                  : <span style={{ color: '#888', fontWeight: 'normal' }}>None</span>}
              </div>
              
             <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '0.95rem',
                  gap: '10px',
                  margin: '10px 0',
                  flexWrap: 'wrap',
                }}
              >
                <span>
                  <strong>Uploader:</strong> {film.uploader_username}
                </span>
                <span>
                  <strong>Uploader's Tokens:</strong> {film.uploader_tokens}
                </span>
                <span>
                  <strong>Feedbacks Received:</strong> {film.review_count}
                </span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '15px' }}>
                <button
                  onClick={() => handleLeaveReview(film.id)}
                  style={{
                    backgroundColor: '#28a745',
                    color: 'white',
                    padding: '10px 15px',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => (e.target.style.backgroundColor = '#218838')}
                  onMouseLeave={(e) => (e.target.style.backgroundColor = '#28a745')}
                >
                  View/Add Feedback
                </button>

                {/*}
                <Link
                  to={`/films/${film.id}`}
                  style={{
                    backgroundColor: '#0056b3',
                    color: 'white',
                    padding: '10px 15px',
                    borderRadius: '5px',
                    textDecoration: 'none',
                    textAlign: 'center',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => (e.target.style.backgroundColor = '#003d80')}
                  onMouseLeave={(e) => (e.target.style.backgroundColor = '#0056b3')}
                >
                  View Details
                </Link>
                */}
                
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p style={{ textAlign: 'center', color: '#666', marginTop: '20px' }}>No public films available.</p>
      )}
  
      {/* Pagination Controls */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: '30px',
          gap: '10px',
        }}
      >
        <button
          onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
          disabled={page === 1}
          style={{
            padding: '10px 20px',
            backgroundColor: page === 1 ? '#ccc' : '#0056b3',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: page === 1 ? 'not-allowed' : 'pointer',
          }}
        >
          Previous
        </button>
        <span style={{ fontWeight: 'bold' }}>Page {page} of {totalPages}</span>
        <button
          onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={page === totalPages}
          style={{
            padding: '10px 20px',
            backgroundColor: page === totalPages ? '#ccc' : '#0056b3',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: page === totalPages ? 'not-allowed' : 'pointer',
          }}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default PublicFilms